import React from 'react';
import { Button } from '@heroui/react';
import { IFileInfo } from '../../types/reportsTypes';
import StatLabel from './components/StatLabel';
import FileTypeDropdown from './components/FileTypeDropdown';
import WarningAlert from './components/WarningAlert';

interface DataPreviewProps {
	fileInfo: IFileInfo;
	onFileInfoChange: (info: IFileInfo) => void;
	isLoading: boolean;
	onDownload: () => void;
	isDataLoaded?: boolean;
	warningMessage?: string;
}

export const DataPreview: React.FC<DataPreviewProps> = ({
	fileInfo,
	onFileInfoChange,
	isLoading,
	onDownload,
	isDataLoaded = false,
	warningMessage,
}) => {
	const handleFileTypeChange = (fileType: string) => {
		onFileInfoChange({
			...fileInfo,
			fileType,
		});
	};

	// Simple check if estimated size is more than 2 GB
	const isSizeExceedsTwoGB = fileInfo.estimatedSize.includes('GB') && parseFloat(fileInfo.estimatedSize.split(' ')[0]) > 2;

	// Check if record count exceeds Excel's row limit (1,048,575 rows)
	const isExcelRowLimitExceeded = fileInfo.fileType === 'xlsx' && fileInfo.recordCount > 1048575;

	const isDownloadDisabled = !isDataLoaded || isSizeExceedsTwoGB || isExcelRowLimitExceeded || !fileInfo.fileType;

	return (
		<div className="flex flex-col h-full gap-2 space-y-4">
			<h2 className="text-xl font-semibold text-msi-midblue-color-500 border-b">Data Preview</h2>
			<div className="w-full h-full flex flex-col overflow-auto">
				<div className="h-full overflow-x-hidden overflow-y-auto mb-5">
					<div className="space-y-2 mb-6">
						<StatLabel label="# records" value={fileInfo.recordCount.toLocaleString()} />
						<StatLabel label="# vessels" value={fileInfo.vesselCount.toLocaleString()} />
						<StatLabel label="First Record" value={fileInfo.firstRecord} />
						<StatLabel label="Last Record" value={fileInfo.lastRecord} />
						<StatLabel label="Est. File Size" value={fileInfo.estimatedSize} />
						{isSizeExceedsTwoGB && fileInfo.fileType && (
							<WarningAlert message="Warning: File size exceeds 2GB limit. Please reduce your selection to enable download." />
						)}
						{isExcelRowLimitExceeded && (
							<WarningAlert
								message={`Warning: Record count (${fileInfo.recordCount.toLocaleString()}) exceeds Excel's maximum row limit (1,048,575). Please reduce your selection or choose a different file format.`}
							/>
						)}
						{warningMessage && <WarningAlert message={warningMessage} />}
					</div>
				</div>

				{/* <div className="flex">
				<FileTypeDropdown value={fileInfo.fileType} onChange={handleFileTypeChange} />
			</div> */}

				<div className="h-1/4 mt-5 space-y-4">
					<div className="space-y-4 w-full">
						<h3 className="text-xl font-semibold text-msi-midblue-color-500 border-b">File Type</h3>
						<FileTypeDropdown value={fileInfo.fileType} onChange={handleFileTypeChange} />
					</div>
				</div>

				<div className="flex justify-end">
					<Button
						color="primary"
						size="sm"
						isLoading={isLoading}
						onPress={onDownload}
						className="rounded"
						isDisabled={isDownloadDisabled}
					>
						{isLoading ? 'Preparing...' : 'Download'}
					</Button>
				</div>
			</div>
		</div>
	);
};
